import { useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '@/i18n';
import {
  Button,
  Card,
  Icon,
  Skeleton,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { useState } from 'react';
import {
  useDataSource,
  useSyncRuns,
  useDeleteDataSource,
  useTriggerSync,
  useTriggerRescan,
  usePauseDataSource,
  useResumeDataSource,
  useBindDataSource,
  useUnbindDataSource,
} from '../hooks/use-datasources';
import { useCollections } from '@/hooks/use-knowledge';
import { STATUS_CONFIG, PROVIDER_LABELS } from '../types';
import type { DataSourceStatus, ProviderType, SyncRun, SyncRunStatus } from '../types';
import { DatasourceProviderIcon } from '../components/ProviderIcons';

// =============================================================================
// Sub-components
// =============================================================================

function StatusBadge({ status }: { status: DataSourceStatus }) {
  const { t } = useI18n();
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.active;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm font-medium ${config.color}`}>
      {config.animate && <Icon name="loader" size={14} className="animate-spin" />}
      {t(`datasources.status.${status}`)}
    </span>
  );
}

const SYNC_STATUS_COLORS: Record<SyncRunStatus, string> = {
  running: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  completed: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  cancelled: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

function SyncRunRow({ run }: { run: SyncRun }) {
  const color = SYNC_STATUS_COLORS[run.status as SyncRunStatus] || SYNC_STATUS_COLORS.cancelled;
  const stats = run.stats as Record<string, number>;

  return (
    <div className="flex items-center gap-4 py-3 border-b last:border-0">
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}>
        {run.status === 'running' && <Icon name="loader" size={12} className="animate-spin mr-1" />}
        {run.status}
      </span>
      <span className="text-xs text-gray-500 uppercase">{run.trigger}</span>
      <div className="flex-1 flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
        {stats.added != null && <span>+{stats.added}</span>}
        {stats.updated != null && <span>~{stats.updated}</span>}
        {stats.deleted != null && <span>-{stats.deleted}</span>}
      </div>
      <span className="text-xs text-gray-500">
        {new Date(run.started_at).toLocaleString()}
      </span>
      {run.error_message && (
        <span className="text-xs text-red-500 truncate max-w-[200px]" title={run.error_message}>
          {run.error_message}
        </span>
      )}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between py-2 border-b last:border-0">
      <span className="text-sm text-gray-500 dark:text-gray-400 shrink-0 pr-4">{label}</span>
      <span className="min-w-0 max-w-[60%] break-all text-sm font-medium text-gray-900 dark:text-gray-100 text-right">
        {value}
      </span>
    </div>
  );
}

// =============================================================================
// DataSource Detail Page
// =============================================================================

export function DataSourceDetailPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [showDelete, setShowDelete] = useState(false);
  const [showBind, setShowBind] = useState(false);
  const [selectedCollectionId, setSelectedCollectionId] = useState('');

  // Queries
  const { data: ds, isLoading, isError, refetch } = useDataSource(id ?? '');
  const { data: runsData } = useSyncRuns(id ?? '');
  const { data: collectionsData } = useCollections({ page_size: 100 });
  const collections = collectionsData?.data ?? [];
  const runs = runsData?.data ?? [];

  // Mutations
  const deleteDS = useDeleteDataSource();
  const syncDS = useTriggerSync();
  const rescanDS = useTriggerRescan();
  const pauseDS = usePauseDataSource();
  const resumeDS = useResumeDataSource();
  const bindDS = useBindDataSource();
  const unbindDS = useUnbindDataSource();

  if (isLoading) {
    return (
      <div className="p-6 max-w-4xl mx-auto space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !ds) {
    return (
      <div className="p-6">
        <Card className="p-8 text-center">
          <Icon name="alert-circle" size={48} className="mx-auto text-red-500 mb-4" />
          <h3 className="text-lg font-medium mb-2">{t('datasources.notFound')}</h3>
          <Button variant="outline" onClick={() => navigate('/knowledge/datasources')}>
            {t('common.goBack')}
          </Button>
        </Card>
      </div>
    );
  }

  const providerConfig = ds.provider_config as Record<string, unknown>;
  const filterConfig = ds.filter_config as Record<string, unknown>;
  const syncConfig = ds.sync_config as Record<string, unknown>;

  const handleDelete = async () => {
    try {
      await deleteDS.mutateAsync(ds.id);
      toast.success(t('datasources.deleteSuccess').replace('{name}', ds.name));
      navigate('/knowledge/datasources');
    } catch {
      toast.error(t('datasources.deleteFailed'));
    }
  };

  const handleBind = async () => {
    if (!selectedCollectionId) return;
    try {
      await bindDS.mutateAsync({ id: ds.id, collection_id: selectedCollectionId });
      toast.success(t('datasources.bindSuccess'));
      setShowBind(false);
      refetch();
    } catch {
      toast.error(t('datasources.bindFailed'));
    }
  };

  const handleUnbind = async () => {
    try {
      await unbindDS.mutateAsync(ds.id);
      toast.success(t('datasources.unbindSuccess'));
      refetch();
    } catch {
      toast.error(t('datasources.unbindFailed'));
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/knowledge/datasources')}>
            <Icon name="arrow-left" size={20} />
          </Button>
          <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30">
            <DatasourceProviderIcon providerType={ds.provider_type as ProviderType} size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{ds.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-gray-500">{PROVIDER_LABELS[ds.provider_type as ProviderType]}</span>
              <StatusBadge status={ds.status as DataSourceStatus} />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(ds.status === 'active' || ds.status === 'error') && (
            <>
              <Button variant="outline" size="sm" onClick={() => syncDS.mutateAsync(ds.id).then(() => { toast.success(t('datasources.syncTriggered')); refetch(); }).catch(() => toast.error(t('datasources.syncFailed')))}>
                <Icon name="refresh-cw" size={14} className="mr-1" />
                {t('datasources.triggerSync')}
              </Button>
              <Button variant="outline" size="sm" onClick={() => pauseDS.mutateAsync(ds.id).then(() => { toast.success(t('datasources.pauseSuccess')); refetch(); }).catch(() => toast.error(t('datasources.pauseFailed')))}>
                <Icon name="pause" size={14} className="mr-1" />
                {t('datasources.pause')}
              </Button>
            </>
          )}
          {ds.status === 'paused' && (
            <Button variant="outline" size="sm" onClick={() => resumeDS.mutateAsync(ds.id).then(() => { toast.success(t('datasources.resumeSuccess')); refetch(); }).catch(() => toast.error(t('datasources.resumeFailed')))}>
              <Icon name="play" size={14} className="mr-1" />
              {t('datasources.resume')}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => navigate(`/knowledge/datasources/${ds.id}/edit`)}>
            <Icon name="edit" size={14} className="mr-1" />
            {t('common.edit')}
          </Button>
          <Button variant="outline" size="sm" className="text-red-600" onClick={() => setShowDelete(true)}>
            <Icon name="trash-2" size={14} className="mr-1" />
            {t('common.delete')}
          </Button>
        </div>
      </div>

      {/* Error Banner */}
      {ds.error_message && (
        <Card className="p-4 mb-6 border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800">
          <div className="flex items-start gap-3">
            <Icon name="alert-triangle" size={20} className="text-red-500 shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-red-800 dark:text-red-300">{t('datasources.errorTitle')}</h4>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">{ds.error_message}</p>
              {ds.consecutive_failures > 1 && (
                <p className="text-xs text-red-500 mt-1">
                  {t('datasources.consecutiveFailures').replace('{n}', String(ds.consecutive_failures))}
                </p>
              )}
            </div>
          </div>
        </Card>
      )}

      {ds.description && (
        <p className="text-gray-600 dark:text-gray-400 mb-6">{ds.description}</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Provider Config */}
        <Card className="p-5">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('datasources.detail.providerConfig')}</h3>
          {Object.entries(providerConfig).map(([key, value]) => (
            <InfoRow key={key} label={key} value={String(value ?? '—')} />
          ))}
        </Card>

        {/* Sync Info */}
        <Card className="p-5">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('datasources.detail.syncInfo')}</h3>
          <InfoRow label={t('datasources.detail.interval')} value={`${syncConfig.interval_minutes ?? 60} min`} />
          <InfoRow label={t('datasources.detail.maxRetries')} value={String(syncConfig.max_retries ?? 3)} />
          <InfoRow label={t('datasources.detail.lastSync')} value={ds.last_sync_at ? new Date(ds.last_sync_at).toLocaleString() : '—'} />
          <InfoRow label={t('datasources.detail.nextSync')} value={ds.next_sync_at ? new Date(ds.next_sync_at).toLocaleString() : '—'} />
          <InfoRow label={t('datasources.detail.failures')} value={String(ds.consecutive_failures)} />
        </Card>

        {/* Filter Config */}
        <Card className="p-5">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('datasources.detail.filterConfig')}</h3>
          <InfoRow
            label={t('datasources.form.extensions')}
            value={((filterConfig.include_extensions as string[]) ?? []).join(', ') || '—'}
          />
          <InfoRow
            label={t('datasources.form.excludePatterns')}
            value={((filterConfig.exclude_patterns as string[]) ?? []).join(', ') || '—'}
          />
          <InfoRow
            label={t('datasources.form.maxFileSize')}
            value={`${Math.round(((filterConfig.max_file_size_bytes as number) ?? 0) / 1048576)} MB`}
          />
          <InfoRow
            label={t('datasources.form.maxDepth')}
            value={String((filterConfig.max_depth as number) ?? 10)}
          />
        </Card>

        {/* Collection Binding */}
        <Card className="p-5">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('datasources.detail.binding')}</h3>
          {ds.bound_collection_id ? (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Icon name="link" size={16} className="text-green-600" />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {collections.find((c) => c.id === ds.bound_collection_id)?.name ?? ds.bound_collection_id}
                </span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => navigate(`/knowledge/collections/${ds.bound_collection_id}`)}>
                  {t('datasources.viewCollection')}
                </Button>
                <Button size="sm" variant="outline" className="text-red-600" onClick={handleUnbind}>
                  {t('datasources.unbind')}
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-sm text-gray-500 mb-3">{t('datasources.notBound')}</p>
              <Button size="sm" variant="outline" onClick={() => setShowBind(true)}>
                <Icon name="link" size={14} className="mr-1" />
                {t('datasources.bindToCollection')}
              </Button>
            </div>
          )}
        </Card>
      </div>

      {/* Sync Runs History */}
      <Card className="p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">{t('datasources.detail.syncHistory')}</h3>
          <Button
            size="sm"
            variant="outline"
            onClick={() => rescanDS.mutateAsync(ds.id).then(() => toast.success(t('datasources.rescanTriggered'))).catch(() => toast.error(t('datasources.rescanFailed')))}
          >
            <Icon name="scan" size={14} className="mr-1" />
            {t('datasources.fullRescan')}
          </Button>
        </div>
        {runs.length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">{t('datasources.noSyncRuns')}</p>
        ) : (
          <div>
            {runs.map((run) => (
              <SyncRunRow key={run.id} run={run} />
            ))}
          </div>
        )}
      </Card>

      {/* Delete Dialog */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('datasources.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('datasources.deleteDescription').replace('{name}', ds.name)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              {deleteDS.isPending ? t('common.deleting') : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bind Dialog */}
      <AlertDialog open={showBind} onOpenChange={setShowBind}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('datasources.bindTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('datasources.bindDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Select value={selectedCollectionId || 'none'} onValueChange={(v) => setSelectedCollectionId(v === 'none' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder={t('datasources.form.selectCollection')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('datasources.form.selectCollection')}</SelectItem>
                {collections.map((col) => (
                  <SelectItem key={col.id} value={col.id}>{col.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleBind} disabled={!selectedCollectionId}>
              {t('datasources.bind')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
